'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
  type FormEvent,
} from 'react';
import { useRouter } from 'next/navigation';
import { message, Spin, Alert } from 'antd';
import { ServiceBase } from '@/lib/services/ServiceBase';
import { authService } from '@/lib/services/AuthService';
import { userService } from '@/lib/services/UserService';
import { LoginStatus, type CurrentUser } from '@/lib/types/auth.types';
import { CurrentUserProvider } from './CurrentUserContext';
import { LoginPage } from '@/components/auth/LoginPage';
import { deleteCurrentToken, getCurrentToken, setCurrentToken } from '@/lib/utils/cookies';
import * as api from '@/lib/api';

// Context for login form
interface LoginFormContext {
  username: string;
  password: string;
  setUsername: (username: string) => void;
  setPassword: (password: string) => void;
  handleSubmit: (e: FormEvent) => Promise<void>;
}

const LoginFormCtx = createContext<LoginFormContext>({} as LoginFormContext);

export function useLoginForm() {
  return useContext(LoginFormCtx);
}

interface LoginContextProps {
  children: ReactNode;
}

/**
 * LoginProvider - Main authentication provider
 * Features:
 * - Session check on mount (healthcheck + token validation)
 * - Auto token renewal every 10 minutes
 * - Login form state management
 * - Conditional rendering based on login status
 */
export const LoginProvider: FC<LoginContextProps> = ({ children }) => {
  const router = useRouter();
  const [mounted, setMounted] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [status, setStatus] = useState<LoginStatus>(LoginStatus.Processing);
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);

  /**
   * Handle login - get current user and set authenticated state
   */
  const handleLogin = useCallback(async (accessToken: string) => {
    setIsAuthenticated(true);
    try {
      // Set token for both ServiceBase and directory API client
      api.setAuthToken(accessToken);

      const currUser = await userService.getCurrentUser(accessToken);
      setCurrentUser({
        ...currUser,
        canAccessAny: (pers) => pers.some((per) => currUser.permissions?.map((item) => item).includes(per)),
        logout: async () => {
          // Clear token and redirect to login
          deleteCurrentToken();
          ServiceBase.setToken(undefined);
          api.setAuthToken(undefined);
          setIsAuthenticated(false);
          setCurrentUser(null);
          setStatus(LoginStatus.Start);
          router.replace('/login');
        },
      });
    } catch (error) {
      setStatus(LoginStatus.Start);
      console.error('Lỗi khi xác thực username và password', error);
      message.error('Username hoặc password không đúng');
    }
  }, [router]);

  /**
   * Renew token - refresh access token
   */
  const renewToken = useCallback(async () => {
    const token = getCurrentToken();
    if (token) {
      try {
        const newToken = await authService.renewToken(token);
        if (newToken) {
          await handleLogin(newToken);
          setStatus(LoginStatus.Completed);
        } else {
          // Refresh failed, redirect to login
          setIsAuthenticated(false);
          setStatus(LoginStatus.Start);
        }
      } catch (error) {
        console.error('Token refresh failed:', error);
        setIsAuthenticated(false);
        setStatus(LoginStatus.Start);
      }
    } else {
      // No token, redirect to login
      setIsAuthenticated(false);
      setStatus(LoginStatus.Start);
    }
  }, [handleLogin]);

  /**
   * Check session on mount
   */
  const checkSession = useCallback(async () => {
    // Healthcheck
    try {
      await fetch(
        `${process.env.NEXT_PUBLIC_BACKEND_API || 'http://localhost:26293/api'}/misc/healthcheck`,
        {
          method: 'GET',
        },
      );
    } catch {
      setStatus(LoginStatus.NetworkError);
      return;
    }

    // Validate token
    try {
      const token = getCurrentToken();

      if (token) {
        await handleLogin(token);
        setIsAuthenticated(true);
        setStatus(LoginStatus.Completed);
      } else {
        setIsAuthenticated(false);
        setStatus(LoginStatus.Start);
      }
    } catch (error) {
      console.error('Failed to check session:', error);
      setIsAuthenticated(false);
      setStatus(LoginStatus.Start);
    }
  }, [handleLogin]);

  /**
   * Configure ServiceBase and setup auto-renewal on mount
   */
  useEffect(() => {
    // Mark component as mounted (client-side only)
    setMounted(true);

    // Configure ServiceBase
    ServiceBase.setConfig(
      process.env.NEXT_PUBLIC_BACKEND_API || 'http://localhost:26293/api',
      {
        error: (msg: string) => message.error(msg),
        warn: (msg: string) => message.warning(msg),
      },
    );

    // Setup token refresher
    ServiceBase.setTokenRefresher(async () => {
      const token = getCurrentToken();
      if (token) {
        const newToken = await authService.renewToken(token);
        ServiceBase.setToken(newToken);
        api.setAuthToken(newToken); // Also set for directory API
        setCurrentToken(newToken, 7); // 7 days expiry
      } else {
        throw new Error('No refresh token available');
      }
    });

    // Check session on mount
    checkSession();

    // Auto-renew token every 10 minutes
    const interval = setInterval(() => {
      renewToken();
    }, 10 * 60 * 1000);

    return () => clearInterval(interval);
  }, [checkSession, renewToken]);

  /**
   * Login form submit handler
   */
  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      try {
        setStatus(LoginStatus.Processing);
        const data = await authService.createSession(username, password);
        if (data.accessToken) {
          await handleLogin(data.accessToken);
          setStatus(LoginStatus.Completed);
          router.replace('/danh-ba');
        } else {
          message.error('Đăng nhập thất bại');
          setStatus(LoginStatus.Start);
        }
      } catch (error) {
        setStatus(LoginStatus.Start);
        console.error('Lỗi khi xác thực username và password', error);
        message.error('Username hoặc password không đúng');
      }
    },
    [handleLogin, password, username, router],
  );

  const loginFormContextValue = useMemo<LoginFormContext>(
    () => ({
      username,
      password,
      setUsername,
      setPassword,
      handleSubmit,
    }),
    [username, password, handleSubmit],
  );

  // Wait for client-side hydration to complete
  if (!mounted) {
    return null;
  }

  // Conditional rendering based on status
  if (status === LoginStatus.Start) {
    return (
      <LoginFormCtx.Provider value={loginFormContextValue}>
        <LoginPage />
      </LoginFormCtx.Provider>
    );
  }

  if (status === LoginStatus.Processing) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (status === LoginStatus.Completed && isAuthenticated && currentUser && currentUser.loginName) {
    return (
      <LoginFormCtx.Provider value={loginFormContextValue}>
        <CurrentUserProvider currentUser={currentUser}>{children}</CurrentUserProvider>
      </LoginFormCtx.Provider>
    );
  }

  if (status === LoginStatus.NetworkError) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Alert
          message="Lỗi kết nối"
          description="Máy chủ đang bận hoặc đang bảo trì, vui lòng quay lại sau."
          type="error"
          showIcon
        />
      </div>
    );
  }

  // Fallback error
  return (
    <div className="flex h-screen items-center justify-center">
      <Alert message="Lỗi không xác định" description="Vui lòng thử lại sau." type="error" showIcon />
    </div>
  );
};

